import React from 'react'
import { Link } from '@scentregroup/shared/link'
import classnames from 'classnames'
import { Container } from '@scentregroup/ui'
import { CentresContext, useUser } from '@scentregroup/shared/context'
import CVPStrip from '@scentregroup/shared/hub-components/cvp-strip'
import { UniversalFooter } from '@scentregroup/shared/components/universal-footer'
import TaskHeader from '@scentregroup/shared/components/task-header'
import { buildBasenameLink } from '@scentregroup/shared/components/basename-link'
import {
  NationalStaticData,
  useUniversalHeader,
} from '@scentregroup/shared/hub-components/universal-header'

import style from './index.module.scss'
import { siteHeadersIds } from './site-headers-ids'
import CheckoutProgress, {
  enableProgressTracker,
  CheckoutProgressStep,
} from '../../checkout-progress'
import PageMetaData from '../../../../shared/component/page-meta-data'
import trackMkpEvents, { labels } from '../../../lib/market-place-track-event'
import { useSiteConfiguration } from '../../../lib/site-configuration'
import { useRouter } from 'next/router'
import TopStrip, {
  ITopStripProps,
  useIsTopStripEnabled,
} from '@scentregroup/shared/hub-components/top-strip'
import trackClickEvents, {
  categories,
} from '@scentregroup/shared/hub-components/analytics/trackEvents'

interface ILayoutProps {
  component: React.ElementType
  isCartOrCheckout?: boolean
  hideBackLink?: boolean
  restProps?: Record<string, unknown>
  pageTitle?: string
  currentStep?: CheckoutProgressStep
  directHeaderData?: NationalStaticData
  noTopPadding?: boolean
  headerHasBottomMargin?: [boolean, boolean, boolean]
  hasCanonical?: boolean
  topStrip: ITopStripProps | null
  showCVPStrip?: boolean
  canonicalParams?: string[]
  canonicalDefaults?: Record<string, string | number>
  showRobotsMetaTag?: boolean
}

const basenameLink = buildBasenameLink(Link, '')

const handleClickTopStrip: React.MouseEventHandler = () => {
  trackClickEvents.clicked(categories.UNIVERSAL_HEADER, labels.BENEFITS_STRIP)
}

const Layout: React.FunctionComponent<
  React.PropsWithChildren<ILayoutProps>
> = ({
  component: Component,
  isCartOrCheckout,
  hideBackLink,
  restProps,
  pageTitle,
  currentStep,
  noTopPadding = false,
  headerHasBottomMargin = [true, true, true],
  hasCanonical = false,
  topStrip: topStripProps,
  showCVPStrip = true,
  canonicalParams = [],
  canonicalDefaults = {},
  directHeaderData = {} as NationalStaticData, // TEMPORARY: while this layout is used by non-next pages
  showRobotsMetaTag = true,
}) => {
  const isTopStripEnabled = useIsTopStripEnabled()
  const siteConfiguration = useSiteConfiguration()

  const { user } = useUser()

  const hasCheckoutProgress = enableProgressTracker(currentStep)

  const [Header, setHeaderNavigationPath] = useUniversalHeader({
    variant: 'direct',
    data: directHeaderData,
    hasBottomMargin: headerHasBottomMargin,
    logoText: null,
  })

  const router = useRouter()
  return (
    <CentresContext.Provider value={directHeaderData.centres ?? []}>
      <div className={style.app}>
        <PageMetaData
          getMetaData={() => {
            return {
              title: `${pageTitle ? pageTitle + ' | ' : ''}Westfield`,
              ...(hasCanonical && {
                canonical: [
                  router.domainLocales?.filter(
                    ({ defaultLocale }) => defaultLocale === router.locale
                  )[0].domain,
                  router.basePath,
                  router.asPath,
                ]
                  .join('/')
                  .replace(/\/{2}/g, '/'),
                canonicalParams,
                canonicalDefaults,
              }),
              showRobotsMetaTag,
            }
          }}
        />
        <nav aria-label="Header">
          {topStripProps && isTopStripEnabled && (
            <TopStrip {...topStripProps} onClick={handleClickTopStrip} />
          )}
          {showCVPStrip && !isTopStripEnabled && (
            <CVPStrip country="Australia" />
          )}
          {hasCheckoutProgress ? (
            <div id={siteHeadersIds} className={style.headerContainer}>
              <TaskHeader
                Link={basenameLink}
                logoType={'westfield-logo-red'}
                user={user}
                back={
                  hideBackLink
                    ? undefined
                    : {
                        label: 'Cart',
                        url: '/shop/cart',
                        onClick: () =>
                          trackMkpEvents.clicked(labels.BACK_TO_CART),
                      }
                }
              />
              <CheckoutProgress currentStep={currentStep} />
            </div>
          ) : (
            <Header />
          )}
        </nav>
        <main>
          <Container
            type="large"
            className={classnames(style.container, {
              [style.cartCheckoutContainer]: isCartOrCheckout,
              [style.noTopPadding]: noTopPadding,
            })}
          >
            <Component {...restProps} />
          </Container>
        </main>
        <UniversalFooter
          Link={basenameLink}
          country={siteConfiguration.country}
          onFindACentre={() => setHeaderNavigationPath(['find-a-centre'])}
          taskMode={hasCheckoutProgress}
        />
      </div>
    </CentresContext.Provider>
  )
}

export default Layout
